import { create } from 'zustand';
import { expertisesApi, parcellesApi, biensApi, etudesMarcheApi, valorisationsApi } from '@/api';

/**
 * Navigue un objet nested par chemin (ex: "parcelles.0.situation.geodesie")
 * et retourne un nouvel objet avec la valeur mise à jour (immutable).
 */
function setNestedValue(obj, path, value) {
  const keys = path.split('.');
  if (keys.length === 1) {
    return { ...obj, [keys[0]]: value };
  }
  const [head, ...rest] = keys;
  const index = Number(head);
  if (Array.isArray(obj) && !isNaN(index)) {
    const copy = [...obj];
    copy[index] = setNestedValue(copy[index] ?? {}, rest.join('.'), value);
    return copy;
  }
  return {
    ...obj,
    [head]: setNestedValue(obj[head] ?? {}, rest.join('.'), value),
  };
}

/**
 * Extrait les champs d'une entité (sans id, created_at, updated_at, relations).
 */
function extractExpertisePayload(expertise) {
  if (!expertise) return null;
  const {
    id: _id, created_at: _created_at, updated_at: _updated_at,
    parcelles: _parcelles, etude_marche: _etude_marche, valorisation: _valorisation,
    ...payload
  } = expertise;
  return payload;
}

function extractParcellePayload(parcelle) {
  if (!parcelle) return null;
  const { id: _id, created_at: _created_at, updated_at: _updated_at, biens: _biens, _geometry, ...payload } = parcelle;
  return payload;
}

function extractBienPayload(bien) {
  if (!bien) return null;
  const { id: _id, created_at: _created_at, updated_at: _updated_at, ...payload } = bien;
  return payload;
}

function extractEtudeMarchePayload(etude) {
  if (!etude) return null;
  const { id: _id, created_at: _created_at, updated_at: _updated_at, transactions: _transactions, ...payload } = etude;
  return payload;
}

function extractValorisationPayload(valo) {
  if (!valo) return null;
  const { id: _id, created_at: _created_at, updated_at: _updated_at, ajustements: _ajustements, ...payload } = valo;
  return payload;
}

const useExpertiseStore = create((set, get) => ({
  // State
  expertise: null,
  loading: false,
  saving: false,
  error: null,
  dirty: false,
  _deletedParcelleIds: [],

  // Charger l'expertise complète (avec parcelles, biens, etc.)
  fetchExpertise: async (id) => {
    set({ loading: true, error: null });
    try {
      const data = await expertisesApi.getComplete(id);
      set({ expertise: data, loading: false, dirty: false });
    } catch (error) {
      set({ error: error.message, loading: false });
    }
  },

  // Remplace l'expertise par une version fraîche (ex: après injection OCR)
  // sans passer par un fetch (l'appelant a déjà les données) ni toucher à _deletedParcelleIds.
  setExpertiseFromServer: (fresh) => {
    set({ expertise: fresh, dirty: false });
  },

  // Mise à jour locale d'un champ nested
  updateField: (path, value) => {
    set((state) => ({
      expertise: setNestedValue(state.expertise, path, value),
      dirty: true,
    }));
  },

  // Ajouter une parcelle au store (sans id = nouvelle, sera créée au save)
  addParcelle: (parcelleData) => {
    set((state) => {
      const parcelles = [...(state.expertise?.parcelles || []), parcelleData];
      return {
        expertise: { ...state.expertise, parcelles },
        dirty: true,
      };
    });
  },

  // Retirer une parcelle du store (si elle a un id, on la marque pour suppression)
  removeParcelle: (index) => {
    set((state) => {
      const parcelles = [...(state.expertise?.parcelles || [])];
      const removed = parcelles.splice(index, 1)[0];
      const deletedIds = [...state._deletedParcelleIds];
      if (removed?.id) {
        deletedIds.push(removed.id);
      }
      return {
        expertise: { ...state.expertise, parcelles },
        _deletedParcelleIds: deletedIds,
        dirty: true,
      };
    });
  },

  // Sauvegarde complète de toutes les entités modifiées
  saveAll: async () => {
    const { expertise } = get();
    if (!expertise) return;

    set({ saving: true, error: null });
    try {
      // 1. Sauvegarder l'expertise
      const expertisePayload = extractExpertisePayload(expertise);
      await expertisesApi.update(expertise.id, expertisePayload);

      // 2. Sauvegarder les parcelles et biens
      const updatedParcelles = [];
      for (const parcelle of expertise.parcelles || []) {
        const parcellePayload = extractParcellePayload(parcelle);
        let savedParcelle = parcelle;
        if (parcelle.id) {
          await parcellesApi.update(parcelle.id, parcellePayload);
        } else {
          // Nouvelle parcelle — créer côté backend
          savedParcelle = await parcellesApi.create({
            ...parcellePayload,
            expertise_id: expertise.id,
          });
        }
        updatedParcelles.push({ ...parcelle, id: savedParcelle.id });

        for (const bien of parcelle.biens || []) {
          const bienPayload = extractBienPayload(bien);
          if (bien.id) {
            await biensApi.update(bien.id, bienPayload);
          }
        }
      }

      // 2b. Supprimer les parcelles retirées
      const { _deletedParcelleIds } = get();
      for (const deletedId of _deletedParcelleIds) {
        await parcellesApi.delete(deletedId);
      }

      // 3. Sauvegarder l'étude de marché
      if (expertise.etude_marche) {
        const emPayload = extractEtudeMarchePayload(expertise.etude_marche);
        if (expertise.etude_marche.id) {
          await etudesMarcheApi.update(expertise.etude_marche.id, emPayload);
        } else {
          const created = await etudesMarcheApi.create({
            ...emPayload,
            expertise_id: expertise.id,
          });
          set((state) => ({
            expertise: { ...state.expertise, etude_marche: created },
          }));
        }
      }

      // 4. Sauvegarder la valorisation
      if (expertise.valorisation) {
        const valoPayload = extractValorisationPayload(expertise.valorisation);
        if (expertise.valorisation.id) {
          await valorisationsApi.update(expertise.valorisation.id, valoPayload);
        } else {
          const created = await valorisationsApi.create({
            ...valoPayload,
            expertise_id: expertise.id,
          });
          set((state) => ({
            expertise: { ...state.expertise, valorisation: created },
          }));
        }
      }

      // Mettre à jour les IDs des parcelles créées et vider les suppressions
      set((state) => ({
        expertise: { ...state.expertise, parcelles: updatedParcelles },
        _deletedParcelleIds: [],
      }));

      set({ saving: false, dirty: false });
    } catch (error) {
      set({ error: error.message, saving: false });
      throw error;
    }
  },
}));

export default useExpertiseStore;
